import React, { useEffect, useState } from 'react';
import {
  captureExternalAppEvidence,
  getExternalAppEvidenceScreenshot,
  getExternalAppListGeometry,
  testExternalAppListGeometry,
  trainExternalAppListGeometry,
} from './externalAppControlApi';
import ScreenshotRegionSelector from './ScreenshotRegionSelector';

function ListGeometryPanel({ session, disabled, onMessage, onError }) {
  const [capture, setCapture] = useState(null);
  const [geometry, setGeometry] = useState(null);
  const [visibleRows, setVisibleRows] = useState(5);
  const [focusedRowIndex, setFocusedRowIndex] = useState(0);
  const [drawMode, setDrawMode] = useState('');
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    getExternalAppListGeometry(session.id).then((saved) => {
      if (!saved) return;
      setGeometry(saved);
      setVisibleRows(saved.visibleRowCount || 5);
    }).catch(() => {});
  }, [session.id]);

  const captureGuide = async () => {
    setBusy('capture');
    try {
      const evidence = await captureExternalAppEvidence(session.id, true);
      const screenshot = await getExternalAppEvidenceScreenshot(session.id, evidence.evidenceId);
      setCapture({ evidenceId: evidence.evidenceId, dataUrl: screenshot.dataUrl });
      setResult(null);
    } catch (error) {
      onError(error.response?.data?.error || error.message || 'Could not capture the app list.');
    } finally {
      setBusy('');
    }
  };

  const finishDraw = (region) => {
    if (!drawMode) return;
    setGeometry((current) => ({ ...(current || {}), [drawMode]: region }));
    setDrawMode('');
  };

  const payloadGeometry = () => {
    const listRegion = geometry?.listRegion;
    return {
      ...(geometry || {}),
      listRegion,
      visibleRowCount: Number(visibleRows),
      rowHeight: listRegion.height / Number(visibleRows),
      rowSpacing: listRegion.height / Number(visibleRows),
      wrapBehavior: geometry?.wrapBehavior || 'unknown',
    };
  };

  const train = async () => {
    if (!capture?.evidenceId || !geometry?.listRegion || !geometry?.markerRegion) {
      onError('Capture the guide, then draw both the list bounds and selection marker.');
      return;
    }
    setBusy('train');
    try {
      const trained = await trainExternalAppListGeometry(session.id, {
        evidenceId: capture.evidenceId,
        focusedRowIndex: Number(focusedRowIndex),
        geometry: payloadGeometry(),
      });
      setGeometry(trained.geometry);
      setResult(trained.sample);
      onMessage('List geometry and focus colour saved. Capture another focus position to verify it.');
    } catch (error) {
      onError(error.response?.data?.error || error.message || 'List training failed.');
    } finally {
      setBusy('');
    }
  };

  const test = async () => {
    setBusy('test');
    try {
      const evidence = await captureExternalAppEvidence(session.id, true);
      const screenshot = await getExternalAppEvidenceScreenshot(session.id, evidence.evidenceId);
      setCapture({ evidenceId: evidence.evidenceId, dataUrl: screenshot.dataUrl });
      const tested = await testExternalAppListGeometry(session.id, { evidenceId: evidence.evidenceId });
      setResult(tested);
      onMessage(tested.matched
        ? `Focused row ${tested.focusedRowIndex + 1} recognized.`
        : `Focus was not recognized: ${tested.reason.replace(/_/g, ' ')}.`);
    } catch (error) {
      onError(error.response?.data?.error || error.message || 'Focus test failed.');
    } finally {
      setBusy('');
    }
  };

  return (
    <details className="apps-launch-recipe">
      <summary>Train list focus and row geometry</summary>
      <p>
        Open the EPG or channel list and leave one row selected. This identifies row positions
        without needing to train every channel separately.
      </p>
      <div className="apps-launch-recipe__heading-actions">
        <button type="button" onClick={captureGuide} disabled={disabled || Boolean(busy)}>
          {busy === 'capture' ? 'Capturing…' : 'Capture visible list'}
        </button>
        {capture && <button type="button" className={drawMode === 'listRegion' ? 'primary' : ''}
          onClick={() => setDrawMode('listRegion')}>1. Draw list bounds</button>}
        {capture && <button type="button" className={drawMode === 'markerRegion' ? 'primary' : ''}
          onClick={() => setDrawMode('markerRegion')}>2. Draw selected-row highlight</button>}
      </div>
      {capture && (
        <ScreenshotRegionSelector
          dataUrl={capture.dataUrl}
          alt="Captured app list"
          active={Boolean(drawMode)}
          onRegion={finishDraw}
          regions={[
            { key: 'list', region: geometry?.listRegion, colour: '#38bdf8' },
            { key: 'marker', region: geometry?.markerRegion, colour: '#f472b6' },
          ]}
        />
      )}
      {capture && (
        <div className="apps-launch-recipe__grid">
          <label>Visible rows <input type="number" min="2" max="30" value={visibleRows}
            onChange={(event) => setVisibleRows(event.target.value)} /></label>
          <label>Currently selected row <input type="number" min="1" max={visibleRows}
            value={Number(focusedRowIndex) + 1}
            onChange={(event) => setFocusedRowIndex(Math.max(0, Number(event.target.value) - 1))} /></label>
          <label>At list end <select value={geometry?.wrapBehavior || 'unknown'}
            onChange={(event) => setGeometry((current) => ({ ...(current || {}), wrapBehavior: event.target.value }))}>
            <option value="unknown">Not tested</option><option value="stops">Focus stops</option><option value="wraps">Focus wraps</option>
          </select></label>
        </div>
      )}
      {capture && <button type="button" className="primary" onClick={train} disabled={disabled || Boolean(busy)}>Save focus training</button>}
      {geometry?.focusedColour && <button type="button" onClick={test} disabled={disabled || Boolean(busy)}>{busy === 'test' ? 'Testing…' : 'Capture and test current focus'}</button>}
      {result && <p>{result.matched ? '✓' : '!'} {result.matched
        ? `Focused row ${result.focusedRowIndex + 1}, confidence ${Math.round(result.confidence * 100)}%`
        : result.reason.replace(/_/g, ' ')}</p>}
    </details>
  );
}

export default ListGeometryPanel;
